// Seasonal Diwali decoration for the app shell. Renders nothing outside the festival window.
// Force on for testing with `?diwali=1`. To remove: delete this file, its usages in AppShell, and the diwali block in index.css.

import { useEffect } from 'react';

// ponytail: fixed window, update the dates each year (Diwali shifts on the lunar calendar)
const START = { month: 9, day: 25 }; // Oct 25 (0-based month)
const END = { month: 10, day: 15 }; // Nov 15

const isDiwaliSeason = (): boolean => {
  if (new URLSearchParams(window.location.search).get('diwali') === '1') return true;
  const now = new Date();
  const md = now.getMonth() * 100 + now.getDate();
  return md >= START.month * 100 + START.day && md <= END.month * 100 + END.day;
};

const active = isDiwaliSeason();

/** Extra classes appended to active nav items; styles live in index.css. */
export const DIWALI_NAV_ACTIVE = active ? 'diwali-nav-active' : '';
export const DIWALI_SUBNAV_ACTIVE = active ? 'diwali-subnav-active' : '';
export const DIWALI_MOBILE_NAV_ACTIVE = active ? 'diwali-mobile-nav-active' : '';

/** Thin marigold garland along the top edge of a positioned parent. */
export const DiwaliToran = () =>
  active ? <div aria-hidden="true" className="diwali-toran pointer-events-none absolute inset-x-0 top-0" /> : null;

/** Small flickering diyas, placed beside the app name. */
export const DiwaliDiyas = () =>
  active ? (
    <span aria-hidden="true" className="flex items-end gap-0.5 ml-1 text-base leading-none select-none">
      <span className="diwali-flicker">🪔</span>
      <span className="diwali-flicker [animation-delay:0.4s]">🪔</span>
    </span>
  ) : null;

const SPARK_COLORS = ['#ef4444', '#f97316', '#facc15', '#fde68a'];
const SPARKS_PER_BURST = 14;
const BURST_GAP_MS = 350;

/** Pops a small firecracker burst near the edge being scrolled toward. */
const burst = (down: boolean) => {
  const x = window.innerWidth * (0.15 + Math.random() * 0.7);
  const y = window.innerHeight * (down ? 0.7 + Math.random() * 0.2 : 0.15 + Math.random() * 0.2);
  for (let i = 0; i < SPARKS_PER_BURST; i++) {
    const s = document.createElement('span');
    const color = SPARK_COLORS[i % SPARK_COLORS.length];
    s.className = 'diwali-spark';
    s.style.cssText = `left:${x}px;top:${y}px;background:${color};box-shadow:0 0 6px 1px ${color}`;
    document.body.appendChild(s);
    const angle = (i / SPARKS_PER_BURST) * Math.PI * 2 + Math.random() * 0.3;
    const dist = 40 + Math.random() * 40;
    s.animate(
      [
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: `translate(${Math.cos(angle) * dist}px,${Math.sin(angle) * dist + 20}px) scale(0.3)`, opacity: 0 },
      ],
      { duration: 700 + Math.random() * 300, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' }
    ).onfinish = () => s.remove();
  }
};

/** Firecracker sparks on scroll (any scroll container), throttled; off for reduced motion. */
export const DiwaliScrollFireworks = () => {
  useEffect(() => {
    if (!active || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let last = 0;
    const lastTop = new WeakMap<EventTarget, number>();
    const onScroll = (e: Event) => {
      const now = Date.now();
      if (now - last < BURST_GAP_MS) return;
      const el = e.target === document ? document.scrollingElement : (e.target as Element);
      if (!el) return;
      const prev = lastTop.get(el) ?? el.scrollTop;
      lastTop.set(el, el.scrollTop);
      if (prev === el.scrollTop) return;
      last = now;
      burst(el.scrollTop > prev);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);
  return null;
};

/** Greeting pill for the header; hidden on small screens. */
export const DiwaliGreeting = () =>
  active ? (
    <span className="diwali-greeting hidden md:inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm shadow-orange-500/30 whitespace-nowrap">
      ✨ Happy Diwali
    </span>
  ) : null;
